import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge, Alert, Spinner,
  Modal, Form, InputGroup, Accordion
} from 'react-bootstrap';
import { qscAPI } from '../utils/api';

const EMPTY_CAT_FORM  = { name: '', description: '' };
const EMPTY_SUB_FORM  = { name: '' };
const EMPTY_ITEM_FORM = { itemCode: '', description: '', subcategoryId: '', criticalPct: '0' };

const QscTemplateManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const hasLoadedRef = useRef(false);

  // Category modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [catModalMode, setCatModalMode] = useState('create');
  const [editingCatId, setEditingCatId] = useState(null);
  const [catForm, setCatForm] = useState(EMPTY_CAT_FORM);
  const [savingCat, setSavingCat] = useState(false);

  // Subcategory modal
  const [showSubModal, setShowSubModal] = useState(false);
  const [subModalMode, setSubModalMode] = useState('create');
  const [editingSubId, setEditingSubId] = useState(null);
  const [subParentCatId, setSubParentCatId] = useState(null);
  const [subForm, setSubForm] = useState(EMPTY_SUB_FORM);
  const [savingSub, setSavingSub] = useState(false);

  // Item modal
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemModalMode, setItemModalMode] = useState('create');
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemParentCatId, setItemParentCatId] = useState(null);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);
  const [savingItem, setSavingItem] = useState(false);

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type, id, name }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadCategories();
    }
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await qscAPI.getCategories();
      setCategories(res.data.data);
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  // ── Category handlers ──────────────────────────────────────────

  const openCreateCategory = () => {
    setCatForm(EMPTY_CAT_FORM);
    setEditingCatId(null);
    setCatModalMode('create');
    setShowCatModal(true);
  };

  const openEditCategory = (cat) => {
    setCatForm({ name: cat.name, description: cat.description || '' });
    setEditingCatId(cat.id);
    setCatModalMode('edit');
    setShowCatModal(true);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setSavingCat(true);
    setError('');
    try {
      if (catModalMode === 'create') {
        await qscAPI.createCategory(catForm);
        showSuccess('เพิ่มหมวดหมู่สำเร็จ');
      } else {
        await qscAPI.updateCategory(editingCatId, catForm);
        showSuccess('แก้ไขหมวดหมู่สำเร็จ');
      }
      setShowCatModal(false);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSavingCat(false);
    }
  };

  // ── Subcategory handlers ───────────────────────────────────────

  const openCreateSubcategory = (catId) => {
    setSubForm(EMPTY_SUB_FORM);
    setEditingSubId(null);
    setSubParentCatId(catId);
    setSubModalMode('create');
    setShowSubModal(true);
  };

  const openEditSubcategory = (sub) => {
    setSubForm({ name: sub.name });
    setEditingSubId(sub.id);
    setSubModalMode('edit');
    setShowSubModal(true);
  };

  const handleSaveSubcategory = async (e) => {
    e.preventDefault();
    setSavingSub(true);
    setError('');
    try {
      if (subModalMode === 'create') {
        await qscAPI.createSubcategory(subParentCatId, subForm);
        showSuccess('เพิ่มหมวดย่อยสำเร็จ');
      } else {
        await qscAPI.updateSubcategory(editingSubId, subForm);
        showSuccess('แก้ไขหมวดย่อยสำเร็จ');
      }
      setShowSubModal(false);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSavingSub(false);
    }
  };

  // ── Item handlers ──────────────────────────────────────────────

  const openCreateItem = (catId) => {
    setItemForm(EMPTY_ITEM_FORM);
    setEditingItemId(null);
    setItemParentCatId(catId);
    setItemModalMode('create');
    setShowItemModal(true);
  };

  const openEditItem = (item, catId) => {
    setItemForm({
      itemCode: item.itemCode,
      description: item.description || '',
      subcategoryId: item.subcategoryId ? String(item.subcategoryId) : '',
      criticalPct: String(item.criticalPct)
    });
    setEditingItemId(item.id);
    setItemParentCatId(catId);
    setItemModalMode('edit');
    setShowItemModal(true);
  };

  const handleSaveItem = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    setError('');
    try {
      const payload = {
        ...itemForm,
        subcategoryId: itemForm.subcategoryId ? parseInt(itemForm.subcategoryId) : null,
        fullScore: 1,
        criticalPct: parseFloat(itemForm.criticalPct) || 0
      };
      if (itemModalMode === 'create') {
        await qscAPI.createItem(itemParentCatId, payload);
        showSuccess('เพิ่มรายการสำเร็จ');
      } else {
        await qscAPI.updateItem(editingItemId, payload);
        showSuccess('แก้ไขรายการสำเร็จ');
      }
      setShowItemModal(false);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setSavingItem(false);
    }
  };

  // ── Delete handler ─────────────────────────────────────────────

  const confirmDelete = (type, id, name) => {
    setDeleteTarget({ type, id, name });
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'category') await qscAPI.deleteCategory(deleteTarget.id);
      else if (deleteTarget.type === 'subcategory') await qscAPI.deleteSubcategory(deleteTarget.id);
      else await qscAPI.deleteItem(deleteTarget.id);
      showSuccess('ลบสำเร็จ');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await loadCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการลบ');
    } finally {
      setDeleting(false);
    }
  };

  const totalItems = categories.reduce((s, c) => s + (c.items?.length || 0), 0);

  // Get subcategories for the item modal's select
  const itemModalCategory = categories.find(c => c.id === itemParentCatId);

  return (
    <Container fluid className="main-content">
      <Row className="mb-4 align-items-center">
        <Col>
          <h2><i className="bi bi-list-check me-2"></i>จัดการ Templates QSC</h2>
          <div className="text-muted">
            {categories.length} หมวดหมู่ · {totalItems} รายการทั้งหมด
          </div>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={openCreateCategory}>
            <i className="bi bi-plus-lg me-1"></i>เพิ่มหมวดหมู่
          </Button>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">กำลังโหลด...</div>
        </div>
      ) : categories.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5 text-muted">
            <i className="bi bi-inbox fs-1 d-block mb-2"></i>
            ยังไม่มีหมวดหมู่ คลิก "เพิ่มหมวดหมู่" เพื่อเริ่มต้น
          </Card.Body>
        </Card>
      ) : (
        <Accordion alwaysOpen>
          {categories.map(cat => {
            // Group items by subcategoryId
            const subGroups = {};
            const noSubItems = [];
            for (const item of (cat.items || [])) {
              if (item.subcategoryId) {
                if (!subGroups[item.subcategoryId]) subGroups[item.subcategoryId] = [];
                subGroups[item.subcategoryId].push(item);
              } else {
                noSubItems.push(item);
              }
            }
            const subMap = {};
            for (const sub of (cat.subcategories || [])) subMap[sub.id] = sub;

            return (
              <Accordion.Item eventKey={String(cat.id)} key={cat.id} className="mb-3">
                <Accordion.Header>
                  <span className="fw-bold me-2">{cat.name}</span>
                  <Badge bg="secondary" className="me-1">{cat.items?.length || 0} รายการ</Badge>
                  <Badge bg="info" text="dark">{cat.subcategories?.length || 0} หมวดย่อย</Badge>
                </Accordion.Header>
                <Accordion.Body>
                  {/* Category description + actions */}
                  <Row className="mb-3 align-items-center">
                    <Col>
                      {cat.description && <span className="text-muted small">{cat.description}</span>}
                    </Col>
                    <Col xs="auto" className="d-flex gap-2">
                      <Button size="sm" variant="outline-info" onClick={() => openCreateSubcategory(cat.id)}>
                        <i className="bi bi-diagram-3 me-1"></i>เพิ่มหมวดย่อย
                      </Button>
                      <Button size="sm" variant="outline-success" onClick={() => openCreateItem(cat.id)}>
                        <i className="bi bi-plus-lg me-1"></i>เพิ่มรายการ
                      </Button>
                      <Button size="sm" variant="outline-primary" onClick={() => openEditCategory(cat)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => confirmDelete('category', cat.id, cat.name)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </Col>
                  </Row>

                  {/* Subcategories */}
                  {(cat.subcategories || []).length > 0 && (
                    <div className="mb-3">
                      <div className="fw-semibold small text-muted mb-2">
                        <i className="bi bi-diagram-3 me-1"></i>หมวดย่อย
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {cat.subcategories.map(sub => (
                          <div key={sub.id} className="border rounded px-3 py-1 d-flex align-items-center gap-2 bg-light">
                            <span className="small fw-semibold">{sub.name}</span>
                            <span className="text-muted small">({(subGroups[sub.id] || []).length} รายการ)</span>
                            <Button size="sm" variant="link" className="p-0 text-primary" onClick={() => openEditSubcategory(sub)}>
                              <i className="bi bi-pencil" style={{ fontSize: '0.75rem' }}></i>
                            </Button>
                            <Button size="sm" variant="link" className="p-0 text-danger" onClick={() => confirmDelete('subcategory', sub.id, sub.name)}>
                              <i className="bi bi-trash" style={{ fontSize: '0.75rem' }}></i>
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Items — no subcategory */}
                  {noSubItems.length > 0 && (
                    <div className="mb-3">
                      {Object.keys(subGroups).length > 0 && (
                        <div className="fw-semibold small text-muted mb-1">
                          <i className="bi bi-list-ul me-1"></i>รายการทั่วไป
                        </div>
                      )}
                      <ItemTable
                        items={noSubItems}
                        catId={cat.id}
                        onEdit={openEditItem}
                        onDelete={confirmDelete}
                      />
                    </div>
                  )}

                  {/* Items grouped by subcategory */}
                  {Object.entries(subGroups).map(([subId, subItems]) => (
                    <div key={subId} className="mb-3">
                      <div className="fw-semibold small text-muted mb-1">
                        <i className="bi bi-diagram-3 me-1"></i>{subMap[subId]?.name || 'หมวดย่อย'}
                      </div>
                      <ItemTable
                        items={subItems}
                        catId={cat.id}
                        onEdit={openEditItem}
                        onDelete={confirmDelete}
                      />
                    </div>
                  ))}

                  {(cat.items || []).length === 0 && (
                    <div className="text-center py-3 text-muted small">
                      <i className="bi bi-inbox me-1"></i>ยังไม่มีรายการ
                    </div>
                  )}
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      )}

      {/* Category Modal */}
      <Modal show={showCatModal} onHide={() => setShowCatModal(false)}>
        <Form onSubmit={handleSaveCategory}>
          <Modal.Header closeButton>
            <Modal.Title>{catModalMode === 'create' ? 'เพิ่มหมวดหมู่ใหม่' : 'แก้ไขหมวดหมู่'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>ชื่อหมวดหมู่ <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={catForm.name} required
                onChange={e => setCatForm(p => ({ ...p, name: e.target.value }))}
                placeholder="เช่น คุณภาพสินค้า"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>คำอธิบาย</Form.Label>
              <Form.Control
                as="textarea" rows={2}
                value={catForm.description}
                onChange={e => setCatForm(p => ({ ...p, description: e.target.value }))}
                placeholder="รายละเอียดเพิ่มเติม..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCatModal(false)} disabled={savingCat}>ยกเลิก</Button>
            <Button variant="primary" type="submit" disabled={savingCat}>
              {savingCat ? <><Spinner size="sm" className="me-1" />กำลังบันทึก...</> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Subcategory Modal */}
      <Modal show={showSubModal} onHide={() => setShowSubModal(false)}>
        <Form onSubmit={handleSaveSubcategory}>
          <Modal.Header closeButton>
            <Modal.Title>{subModalMode === 'create' ? 'เพิ่มหมวดย่อย' : 'แก้ไขหมวดย่อย'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>ชื่อหมวดย่อย <span className="text-danger">*</span></Form.Label>
              <Form.Control
                value={subForm.name} required
                onChange={e => setSubForm(p => ({ ...p, name: e.target.value }))}
                placeholder="เช่น วัตถุดิบ"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSubModal(false)} disabled={savingSub}>ยกเลิก</Button>
            <Button variant="primary" type="submit" disabled={savingSub}>
              {savingSub ? <><Spinner size="sm" className="me-1" />กำลังบันทึก...</> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Item Modal */}
      <Modal show={showItemModal} onHide={() => setShowItemModal(false)} size="lg">
        <Form onSubmit={handleSaveItem}>
          <Modal.Header closeButton>
            <Modal.Title>{itemModalMode === 'create' ? 'เพิ่มรายการใหม่' : 'แก้ไขรายการ'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>รหัสรายการ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    value={itemForm.itemCode} required
                    onChange={e => setItemForm(p => ({ ...p, itemCode: e.target.value }))}
                    placeholder="เช่น Q1.1"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>หมวดย่อย</Form.Label>
                  <Form.Select
                    value={itemForm.subcategoryId}
                    onChange={e => setItemForm(p => ({ ...p, subcategoryId: e.target.value }))}
                  >
                    <option value="">-- ไม่มีหมวดย่อย --</option>
                    {(itemModalCategory?.subcategories || []).map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>คำอธิบาย</Form.Label>
                  <Form.Control
                    as="textarea" rows={2}
                    value={itemForm.description}
                    onChange={e => setItemForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="รายละเอียดรายการประเมิน..."
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Critical % สูงสุด <small className="text-muted">(0 = ไม่มี)</small>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number" min="0" max="100" step="1"
                      value={itemForm.criticalPct}
                      onChange={e => setItemForm(p => ({ ...p, criticalPct: e.target.value }))}
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowItemModal(false)} disabled={savingItem}>ยกเลิก</Button>
            <Button variant="primary" type="submit" disabled={savingItem}>
              {savingItem ? <><Spinner size="sm" className="me-1" />กำลังบันทึก...</> : 'บันทึก'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันการลบ</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>ต้องการลบ <strong>"{deleteTarget?.name}"</strong> ใช่หรือไม่?</p>
          {deleteTarget?.type === 'category' && (
            <Alert variant="warning" className="mb-0 small">
              <i className="bi bi-exclamation-triangle me-1"></i>
              การลบหมวดหมู่จะซ่อนหมวดย่อยและรายการทั้งหมดในหมวดนี้ด้วย
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>ยกเลิก</Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? <><Spinner size="sm" className="me-1" />กำลังลบ...</> : 'ยืนยันลบ'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

// Small reusable item table component
const ItemTable = ({ items, catId, onEdit, onDelete }) => (
  <Table hover size="sm" className="mb-0 border">
    <thead className="table-light">
      <tr>
        <th style={{ width: '80px' }}>รหัส</th>
        <th>คำอธิบาย</th>
        <th style={{ width: '100px' }} className="text-center">Critical %</th>
        <th style={{ width: '80px' }} className="text-center"></th>
      </tr>
    </thead>
    <tbody>
      {items.map(item => (
        <tr key={item.id}>
          <td><code>{item.itemCode}</code></td>
          <td style={{ fontSize: '0.875rem' }}>{item.description}</td>
          <td className="text-center">
            {parseFloat(item.criticalPct) > 0
              ? <Badge bg="danger">{item.criticalPct}%</Badge>
              : <span className="text-muted">-</span>}
          </td>
          <td className="text-center">
            <Button size="sm" variant="outline-primary" className="me-1" onClick={() => onEdit(item, catId)}>
              <i className="bi bi-pencil"></i>
            </Button>
            <Button size="sm" variant="outline-danger" onClick={() => onDelete('item', item.id, item.itemCode)}>
              <i className="bi bi-trash"></i>
            </Button>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);

export default QscTemplateManager;
